import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Input,
  HStack,
  VStack,
  FormControl,
  FormLabel,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  MenuDivider,
  Text,
  useToast,
  useColorMode,
} from '@chakra-ui/react';
import { ChevronDownIcon } from '@chakra-ui/icons';
import axios from '../utils/axiosConfig';

const PhoneNumberManager = () => {
  const { colorMode } = useColorMode();
  const [selectedCountry, setSelectedCountry] = useState({ code: '+65', name: 'Singapore', flag: '🇸🇬' });
  const [newPhoneNumber, setNewPhoneNumber] = useState('');
  const [phoneNumbers, setPhoneNumbers] = useState([]); // list of phone numbers [{countryCode, phoneNumber}]
  const [loading, setLoading] = useState(false);
  const [selectedPhone, setSelectedPhone] = useState(null); // For editing
  const toast = useToast();

  const countryCodes = [
    { code: '+65', name: 'Singapore', flag: '🇸🇬' },
    { code: '+86', name: 'China', flag: '🇨🇳' },
    { code: '+91', name: 'India', flag: '🇮🇳' },
    { code: '+62', name: 'Indonesia', flag: '🇮🇩' },
    { code: '+81', name: 'Japan', flag: '🇯🇵' },
    { code: '+82', name: 'South Korea', flag: '🇰🇷' },
    { code: '+63', name: 'Philippines', flag: '🇵🇭' },
    { code: '+66', name: 'Thailand', flag: '🇹🇭' },
    { code: '+84', name: 'Vietnam', flag: '🇻🇳' },
    { code: '+95', name: 'Myanmar', flag: '🇲🇲' },
    { code: '+880', name: 'Bangladesh', flag: '🇧🇩' },
    { code: '+977', name: 'Nepal', flag: '🇳🇵' },
    { code: '+94', name: 'Sri Lanka', flag: '🇱🇰' },
    { code: '+92', name: 'Pakistan', flag: '🇵🇰' },
    { code: '+960', name: 'Maldives', flag: '🇲🇻' },
    { code: '+670', name: 'Timor-Leste', flag: '🇹🇱' },
    { code: '+855', name: 'Cambodia', flag: '🇰🇭' },
    { code: '+856', name: 'Laos', flag: '🇱🇦' },
    { code: '+993', name: 'Turkmenistan', flag: '🇹🇲' },
    { code: '+998', name: 'Uzbekistan', flag: '🇺🇿' },
    { code: '+992', name: 'Tajikistan', flag: '🇹🇯' },
    { code: '+996', name: 'Kyrgyzstan', flag: '🇰🇬' },
    { code: '+995', name: 'Georgia', flag: '🇬🇪' },
    { code: '+374', name: 'Armenia', flag: '🇦🇲' },
    { code: '+90', name: 'Turkey', flag: '🇹🇷' },
    { code: '+971', name: 'United Arab Emirates', flag: '🇦🇪' },
    { code: '+973', name: 'Bahrain', flag: '🇧🇭' },
    { code: '+974', name: 'Qatar', flag: '🇶🇦' },
    { code: '+968', name: 'Oman', flag: '🇴🇲' },
    { code: '+966', name: 'Saudi Arabia', flag: '🇸🇦' },
    { code: '+964', name: 'Iraq', flag: '🇮🇶' },
    { code: '+98', name: 'Iran', flag: '🇮🇷' },
    { code: '+972', name: 'Israel', flag: '🇮🇱' },
    { code: '+962', name: 'Jordan', flag: '🇯🇴' },
    { code: '+961', name: 'Lebanon', flag: '🇱🇧' },
    { code: '+963', name: 'Syria', flag: '🇸🇾' },
    { code: '+970', name: 'Palestine', flag: '🇵🇸' },
    { code: '+93', name: 'Afghanistan', flag: '🇦🇫' },
    { code: '+994', name: 'Azerbaijan', flag: '🇦🇿' },
  ];

  useEffect(() => {
    const fetchPhoneNumbers = async () => {
      setLoading(true);
      try {
        const response = await axios.get('/api/protected/settings/phone-numbers');
        setPhoneNumbers(response.data.data);
      } catch (error) {
        console.error('Error fetching phone numbers:', error);
      } finally {
        setLoading(false);
      }
    };
  
    fetchPhoneNumbers();
  }, []);

  const handleAddPhoneNumber = async () => {
    setLoading(true);
    try {
      const newPhone = {
        countryCode: selectedCountry.code,
        phoneNumber: newPhoneNumber
      }
      const response = await axios.post('/api/protected/settings/phone-numbers', {
        ...newPhone,
      });
      setPhoneNumbers(response.data.data); // Update state with new phone numbers
      toast({
        title: 'Phone Number Added',
        description: 'The phone number has been added successfully',
        status: 'success',
      });
    } catch (error) {
      console.error('Error adding phone number:', error);
      toast({
        title: 'Error',
        description: 'Failed to add phone number. Please try again.',
        status: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEditPhoneNumber = async () => {
    setLoading(true);
    try {
      const response = await axios.put(`/api/protected/settings/phone-numbers/${selectedPhone._id}`, {
        ...selectedPhone,
      });
      setPhoneNumbers(response.data.data); // Update state
      toast({
        title: 'Phone Number Updated',
        description: 'The phone number has been updated successfully',
        status: 'success',
      });
    } catch (error) {
      console.error('Error updating phone number:', error);
      toast({
        title: 'Error',
        description: 'Failed to update phone number. Please try again.',
        status: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeletePhoneNumber = async (phoneId) => {
    setLoading(true);
    try {
      const response = await axios.delete(`/api/protected/settings/phone-numbers/${phoneId}`);
      setPhoneNumbers(response.data.data); // Update state
      toast({
        title: 'Phone Number Deleted',
        description: 'The phone number has been deleted successfully',
        status: 'success',
      });
    } catch (error) {
      console.error('Error deleting phone number:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete phone number. Please try again.',
        status: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = () => {
    if (selectedPhone) {
      handleEditPhoneNumber(selectedPhone);
    } else {
      handleAddPhoneNumber(newPhoneNumber);
    }
    setSelectedPhone(null); // Clear selected phone after operation
  };

  return (
    <Box p={6}>
      <FormControl mb={4}>
        <FormLabel color={colorMode === 'light' ? 'gray.700' : 'gray.200'}>New Phone Number</FormLabel>
        <HStack>
          <Menu>
            <MenuButton 
              as={Button} 
              rightIcon={<ChevronDownIcon />}  
              size="lg" 
              width="250px"
              bg={colorMode === 'light' ? 'gray.100' : 'gray.700'}
              color={colorMode === 'light' ? 'gray.800' : 'white'}
              _hover={{
                bg: colorMode === 'light' ? 'gray.200' : 'gray.600'
              }}
            >
              <HStack>
                <Text fontSize="lg">{selectedCountry.flag}</Text>
                <Text>{selectedCountry.code}</Text>
              </HStack>
            </MenuButton>
            <MenuList 
              maxH="300px" 
              overflowY="auto"
              bg={colorMode === 'light' ? 'white' : 'gray.700'}
              borderColor={colorMode === 'light' ? 'gray.200' : 'gray.600'}
            >
              {countryCodes.map((country) => (
                <MenuItem
                  key={country.code}
                  onClick={() => setSelectedCountry(country)}
                  display="flex"
                  alignItems="center"
                  _hover={{
                    bg: colorMode === 'light' ? 'gray.100' : 'gray.600'
                  }}
                >
                  <Text fontSize="lg" mr={2}>
                    {country.flag}
                  </Text>
                  <Text color={colorMode === 'light' ? 'gray.800' : 'white'}>
                    {`${country.code} (${country.name})`}
                  </Text>
                </MenuItem>
              ))}
            </MenuList>
          </Menu>
          <Input
            placeholder="Enter phone number"
            value={newPhoneNumber}
            onChange={(e) => setNewPhoneNumber(e.target.value)}
            bg={colorMode === 'light' ? 'white' : 'gray.700'}
            color={colorMode === 'light' ? 'gray.800' : 'white'}
            borderColor={colorMode === 'light' ? 'gray.200' : 'gray.600'}
            _hover={{
              borderColor: colorMode === 'light' ? 'gray.300' : 'gray.500'
            }}
            _focus={{
              borderColor: 'brand.500',
              boxShadow: `0 0 0 1px ${colorMode === 'light' ? '#0066ff' : '#2288ff'}`
            }}
          />
          <Button 
            colorScheme="brand" 
            onClick={handleAddPhoneNumber}
            isLoading={loading}
            loadingText="Adding..."
          >
            Add
          </Button>
        </HStack>
      </FormControl>

      <VStack spacing={3} align="stretch">
        {phoneNumbers.map((number, index) => (
          <HStack
            key={number._id || index}
            justifyContent="space-between"
            bg={colorMode === 'light' ? 'gray.50' : 'gray.700'}
            p={3}
            borderRadius="md"
            borderWidth="1px"
            borderColor={colorMode === 'light' ? 'gray.200' : 'gray.600'}
            _hover={{
              bg: colorMode === 'light' ? 'gray.100' : 'gray.600'
            }}
            transition="all 0.2s"
          >
            <Box>
              <Text 
                fontWeight="bold"
                color={colorMode === 'light' ? 'gray.800' : 'white'}
              >
                {number.phoneNumber}
              </Text>
              <Text 
                fontSize="sm" 
                color={colorMode === 'light' ? 'gray.600' : 'gray.400'}
              >
                Country Code: {number.countryCode}{" "}
                {(() => {
                  const country = countryCodes.find((c) => c.code === number.countryCode);
                  return country ? country.flag : "🏳️";
                })()}
              </Text>
            </Box>

            <HStack>
              <Button
                colorScheme="red"
                size="sm"
                onClick={() => handleDeletePhoneNumber(number._id)}
                isLoading={loading}
              >
                Delete
              </Button>
            </HStack>
          </HStack>
        ))}
      </VStack>
    </Box>
  );
};

export default PhoneNumberManager;